import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const Panel = ({ title, note, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
    <h2 className="text-lg font-bold text-ink">{title}</h2>
    {note && <p className="mt-0.5 text-[12px] text-ink-muted">{note}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Rank = ({ n }) => <span className="w-7 shrink-0 text-[12px] font-bold text-gold tabular-nums">{n}.</span>;

const Stat = ({ label, value, sub }) => (
  <div className="bg-ink text-white rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">{label}</p>
    <p className="mt-1 text-3xl font-bold leading-none">{value}</p>
    {sub && <p className="mt-1.5 text-[12px] text-white/60">{sub}</p>}
  </div>
);

export const NumberOnesRecords = ({ chart = "uk_singles" }) => {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/number-ones/records", { params: { chart } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [chart]);
  if (d === null) return <p className="mt-8 text-sm text-ink-muted">Crunching 74 years of charts…</p>;
  if (!d) return <p className="mt-8 text-sm text-ink-muted">Records are unavailable right now.</p>;

  return (
    <div data-testid="no1-records" className="mt-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Chart weeks" value={d.total_weeks.toLocaleString()} sub={`since ${d.first?.start_label}`} />
        <Stat label="Different No.1 runs" value={d.total_runs.toLocaleString()} sub={`${d.label} · one row per spell at the top`} />
        <Stat label="One-week wonders" value={d.one_week_wonders.count} sub={`${d.kind}s that lasted a single week`} />
        <Stat label="Current No.1" value={d.latest?.title} sub={`${d.latest?.artist} · ${d.latest?.weeks} wk${d.latest?.weeks === 1 ? "" : "s"}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Panel title="Longest unbroken runs" note="Consecutive weeks at No.1 in one spell." testid="records-longest">
          <ol className="space-y-2">
            {d.longest_runs.map((r, i) => (
              <li key={r.start} className="flex items-start gap-2">
                <Rank n={i + 1} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-ink truncate">{r.title}</span>
                  <span className="block text-[12px] text-ink-muted truncate">{r.artist} · {r.start_label} – {r.end_label}</span>
                </span>
                <span className="shrink-0 text-[15px] font-bold text-gold">{r.weeks} wks</span>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel title={`Most No.1 ${d.kind}s`} note="Counted by separate chart-topping runs, exact artist credit." testid="records-most">
          <ol className="space-y-2">
            {d.most_number_ones.map((a, i) => (
              <li key={a.artist} className="flex items-center gap-2">
                <Rank n={i + 1} />
                <span className="flex-1 text-[14px] font-semibold text-ink truncate">{a.artist}</span>
                <span className="text-[15px] font-bold text-gold">{a.count}</span>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel title="Most weeks at No.1" note={`Total chart weeks at the top across all ${d.kind}s.`} testid="records-weeks">
          <ol className="space-y-2">
            {d.most_weeks.map((a, i) => (
              <li key={a.artist} className="flex items-center gap-2">
                <Rank n={i + 1} />
                <span className="flex-1 text-[14px] font-semibold text-ink truncate">{a.artist}</span>
                <span className="text-[15px] font-bold text-gold">{a.weeks} wks</span>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel title="Busiest & quietest years" note={`How many different ${d.kind}s reached No.1 in a year.`} testid="records-years">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-ink-muted mb-2">Most turnover</p>
              {d.busiest_years.map((y) => <p key={y.year} className="flex justify-between text-[14px] text-ink py-0.5"><span className="font-semibold">{y.year}</span><span className="text-gold font-bold">{y.count}</span></p>)}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-ink-muted mb-2">Fewest</p>
              {d.quietest_years.map((y) => <p key={y.year} className="flex justify-between text-[14px] text-ink py-0.5"><span className="font-semibold">{y.year}</span><span className="text-gold font-bold">{y.count}</span></p>)}
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Christmas No.1s" note={`The ${d.kind} at the top of the chart on 25 December each year${d.chart === "uk_singles" ? " — the UK's great festive race" : ""}.`} testid="records-christmas">
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
          {d.christmas_number_ones.map((x) => (
            <li key={x.year} className="flex items-baseline gap-3 text-[13px] py-1 border-b border-[#f5f5f5]">
              <span className="w-10 shrink-0 font-bold text-gold tabular-nums">{x.year}</span>
              <span className="min-w-0 flex-1 truncate"><span className="font-semibold text-ink">{x.title}</span> <span className="text-ink-muted">· {x.artist}</span></span>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Recent one-week wonders" note="Blink and you missed them." testid="records-one-week">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {d.one_week_wonders.sample.map((r) => (
            <li key={r.start} className="flex items-baseline gap-3 text-[13px] py-1 border-b border-[#f5f5f5]">
              <span className="w-24 shrink-0 text-ink-muted">{r.start_label}</span>
              <span className="min-w-0 flex-1 truncate"><span className="font-semibold text-ink">{r.title}</span> <span className="text-ink-muted">· {r.artist}</span></span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};
